// === iCalendar (.ics) Export ===
//
// Builds RFC-5545 VEVENTs for advisory tasks so farmers can drop them into
// Google / Apple / Outlook calendars with a reminder. All-day events, placed
// on the 1st of each relevant (anchor-shifted) month, with a VALARM one day
// before. Year-round tasks become a monthly-recurring event.

import store from '../state/store.js';
import { getShiftedMonths } from '../services/filter-service.js';
import { MONTH_NAMES } from './date.js';

// Escape per RFC 5545 §3.3.11 (TEXT): backslash, semicolon, comma, newline.
function esc(text) {
  return String(text || '')
    .replace(/\\/g, '\\\\')
    .replace(/;/g, '\\;')
    .replace(/,/g, '\\,')
    .replace(/\r?\n/g, '\\n');
}

function pad(n) {
  return String(n).padStart(2, '0');
}

// YYYYMMDD for an all-day DATE value.
function dateValue(year, month1, day) {
  return `${year}${pad(month1)}${pad(day)}`;
}

// A stable-ish UID. We avoid Math.random for determinism across a single
// export; index keeps multi-month events unique.
function uid(item, month1, idx) {
  return `${item.id}-${month1}-${idx}@advisory-calendar`;
}

function addDays(d, n) {
  return new Date(d.getFullYear(), d.getMonth(), d.getDate() + n);
}

// The date an advisory for `month1` should land on, given "now":
// the 1st of that month (this year, or next year if the month has already
// passed) — but never earlier than tomorrow. So a task added mid-month
// lands tomorrow as a "do this soon" nudge rather than on a date already
// gone by; a task for a future month lands on that month's 1st.
function targetDateFor(month1, now) {
  const curMonth1 = now.getMonth() + 1;
  const year = month1 < curMonth1 ? now.getFullYear() + 1 : now.getFullYear();
  const firstOfMonth = new Date(year, month1 - 1, 1);
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const tomorrow = addDays(today, 1);
  return firstOfMonth >= tomorrow ? firstOfMonth : tomorrow;
}

function buildEvent({ item, dtStamp, start, recurring, idx }) {
  const summary = esc(item.content.title);
  const tasks = (item.tasks || []).map(t => `• ${t.label}`).join('\n');
  const descParts = [item.content.summary, tasks && `\nKey actions:\n${tasks}`]
    .filter(Boolean).join('\n');
  const description = esc(descParts);

  const end = addDays(start, 1); // all-day DTEND is exclusive → next day
  const startStr = dateValue(start.getFullYear(), start.getMonth() + 1, start.getDate());
  const endStr = dateValue(end.getFullYear(), end.getMonth() + 1, end.getDate());

  const lines = [
    'BEGIN:VEVENT',
    `UID:${uid(item, start.getMonth() + 1, idx)}`,
    `DTSTAMP:${dtStamp}`,
    `DTSTART;VALUE=DATE:${startStr}`,
    `DTEND;VALUE=DATE:${endStr}`,
  ];
  if (recurring) lines.push('RRULE:FREQ=MONTHLY');
  lines.push(
    `SUMMARY:${summary}`,
    `DESCRIPTION:${description}`,
    'BEGIN:VALARM',
    'TRIGGER:-P1D',
    'ACTION:DISPLAY',
    `DESCRIPTION:${summary}`,
    'END:VALARM',
    'END:VEVENT'
  );
  return lines;
}

// Fold long lines to <=75 octets per RFC 5545 §3.1 (simple char-based fold).
function fold(line) {
  if (line.length <= 75) return line;
  const out = [];
  let s = line;
  out.push(s.slice(0, 75));
  s = s.slice(75);
  while (s.length > 74) {
    out.push(' ' + s.slice(0, 74));
    s = s.slice(74);
  }
  if (s.length) out.push(' ' + s);
  return out.join('\r\n');
}

function wrapCalendar(eventLineGroups) {
  const head = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//Animal Health Ireland//Herd Health Advisory Calendar//EN',
    'CALSCALE:GREGORIAN',
    'METHOD:PUBLISH',
  ];
  const tail = ['END:VCALENDAR'];
  const all = [...head, ...eventLineGroups.flat(), ...tail];
  return all.map(fold).join('\r\n');
}

function nowStamp(now) {
  return `${now.getUTCFullYear()}${pad(now.getUTCMonth() + 1)}${pad(now.getUTCDate())}T${pad(now.getUTCHours())}${pad(now.getUTCMinutes())}${pad(now.getUTCSeconds())}Z`;
}

function triggerDownload(icsText, filename) {
  const blob = new Blob([icsText], { type: 'text/calendar;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

function safeFilename(text) {
  return String(text || 'tasks').replace(/[^a-z0-9]+/gi, '-').replace(/^-+|-+$/g, '').toLowerCase();
}

// Export a single advisory as one or more events (one per relevant month).
export function exportCardIcs(item) {
  const now = new Date();
  const dtStamp = nowStamp(now);
  const yearRound = !!item.meta.yearRound;

  let groups;
  if (yearRound) {
    const start = targetDateFor(now.getMonth() + 1, now);
    groups = [buildEvent({ item, dtStamp, start, recurring: true, idx: 0 })];
  } else {
    const months = getShiftedMonths(item) || item.meta.months || [];
    const unique = [...new Set(months)];
    groups = unique.map((m, idx) =>
      buildEvent({ item, dtStamp, start: targetDateFor(m, now), recurring: false, idx })
    );
  }

  triggerDownload(wrapCalendar(groups), `${safeFilename(item.content.title)}.ics`);
}

// Export every advisory currently visible for the selected month.
export function exportMonthIcs(items, monthIndex0) {
  const now = new Date();
  const dtStamp = nowStamp(now);
  const month1 = monthIndex0 + 1;
  const start = targetDateFor(month1, now);

  const groups = items.map((item, idx) =>
    buildEvent({ item, dtStamp, start, recurring: !!item.meta.yearRound, idx })
  );
  if (!groups.length) return;

  const monthName = MONTH_NAMES[monthIndex0];
  triggerDownload(wrapCalendar(groups), `herd-health-${safeFilename(monthName)}.ics`);
}
